class RiseHero extends HTMLElement {
  #current = 0;
  #timer = null;
  #animating = false;
  #paused = false;
  #hoverPause = false;
  #reduceMotion = false;

  connectedCallback() {
    this.track = this.querySelector('[data-rise-hero-track]');
    this.slides = Array.from(this.querySelectorAll('[data-rise-hero-slide]'));
    this.pills = Array.from(this.querySelectorAll('[data-rise-hero-pill]'));

    const delayAttr = Number(this.dataset.delay);
    const speedAttr = Number(this.dataset.speed);
    this.speed = Number.isFinite(speedAttr) && speedAttr > 0 ? speedAttr : 800;
    this.delay = Number.isFinite(delayAttr) && delayAttr > 0 ? Math.max(delayAttr, this.speed + 200) : 5000;

    this.#reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#hoverPause = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    if (this.slides.length <= 1) {
      this.updatePills(0);
      return;
    }

    this.pills.forEach((pill, index) => {
      pill.addEventListener('click', () => this.goTo(index, !this.#reduceMotion));
    });

    if (this.#hoverPause) {
      this.addEventListener('pointerenter', this.#onPointerEnter);
      this.addEventListener('pointerleave', this.#onPointerLeave);
    }

    document.addEventListener('visibilitychange', this.#onVisibility);
    window.addEventListener('resize', this.#onResize);

    this.goTo(0, false);
    this.play();
  }

  disconnectedCallback() {
    this.pause();
    document.removeEventListener('visibilitychange', this.#onVisibility);
    window.removeEventListener('resize', this.#onResize);
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
  }

  #onPointerEnter = () => {
    this.#paused = true;
    this.pause();
  };

  #onPointerLeave = () => {
    this.#paused = false;
    this.play();
  };

  #onVisibility = () => {
    if (document.hidden) {
      this.pause();
      return;
    }
    if (!this.#paused) this.play();
  };

  #onResize = () => {
    this.goTo(this.#current, false);
  };

  play() {
    if (this.slides.length <= 1 || this.#paused || document.hidden) return;
    this.pause();
    this.#timer = window.setTimeout(() => {
      this.#timer = null;
      this.next();
    }, this.delay);
  }

  pause() {
    if (this.#timer != null) {
      window.clearTimeout(this.#timer);
      this.#timer = null;
    }
  }

  next() {
    this.goTo((this.#current + 1) % this.slides.length, !this.#reduceMotion);
  }

  goTo(index, animate = true) {
    if (!this.slides.length) return;

    if (this.#animating && animate) {
      // Keep autoplay alive if a transition blocked this step
      if (!this.#paused) this.play();
      return;
    }

    const nextIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
    this.#current = nextIndex;

    if (this.track) {
      const offsetPx = nextIndex * this.clientWidth;

      if (animate && !this.#reduceMotion) {
        this.#animating = true;
        this.track.style.transitionDuration = `${this.speed}ms`;
        window.setTimeout(() => {
          this.#animating = false;
        }, this.speed);
      } else {
        this.track.style.transitionDuration = '0ms';
        this.#animating = false;
      }

      this.track.style.transform = `translate3d(-${offsetPx}px, 0, 0)`;
    }

    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === nextIndex ? 'false' : 'true');
    });

    this.updatePills(nextIndex);

    if (!this.#paused) this.play();
  }

  updatePills(activeIndex) {
    this.pills.forEach((pill, index) => {
      const fill = pill.querySelector('[data-rise-hero-pill-fill]');
      const isActive = index === activeIndex;
      pill.classList.toggle('is-active', isActive);
      pill.setAttribute('aria-current', isActive ? 'true' : 'false');

      if (!fill) return;

      fill.style.animation = 'none';
      void fill.offsetWidth;
      fill.style.transform = 'scaleX(0)';

      if (isActive && this.slides.length > 1 && !this.#paused) {
        fill.style.animation = `rise-hero-pill-progress ${this.delay}ms linear forwards`;
      }
    });
  }
}

if (!customElements.get('rise-hero-component')) {
  customElements.define('rise-hero-component', RiseHero);
}
